import React, { useContext, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import TileButton from 'components/atoms/TileButton';
import { CustomPriceSimulationInputTable } from '../CreatePriceSimulator/steps/IdentifyOpportunitiesStep';
import {
    getGoalsColumnsForSimulatorComparison,
    getInputPriceSimulatorColumns,
    getOutputPriceSimulatorColumns,
} from 'utils/getPriceSimulatorColumns';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig, reverseObjectKeyValues } from 'utils/helperMethods';
import { ConfigKeys } from 'utils/Enums';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import {
    getCombinedTableData,
    priceSimulatorScenarioGoalsTable,
    priceSimulatorScenarioInputTable,
    priceSimulatorScenarioOutputTable,
} from 'parser/graphToExcelDownloadParser';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PSR_COMP_MORE_INFO_TILE_CHANGED } from 'utils/mixpanelEventNames';

const scenarioInformationTileData = [
    { key: 'input', label: 'Input' },
    { key: 'output', label: 'Output' },
    { key: 'goals', label: 'Goals' },
];

type ScenarioInformationType = {
    cardDetails: any;
};

const ScenarioInformation = ({ cardDetails }: ScenarioInformationType) => {
    const { appConfig } = useContext(UserContext);
    const { levels, inputKeyMapping, outputKeyMapping, recommendationKeyMapping } = getConfig(
        ConfigKeys.PRICE_SIMULATOR,
        appConfig,
    );
    const [scenarioInfoSelectedTile, setScenarioInfoSelectedTile] = useState(scenarioInformationTileData[0]);
    const [inputKpiOptions, setInputKpiOptions] = useState<any[]>(initializeInputKpisForMultiSelect);
    const [level1Index, setLevel1Index] = useState<any>(null);
    const [level2Index, setLevel2Index] = useState<any>(null);

    const inputMemoisedColumns = useMemo(() => {
        if (cardDetails?.data?.inputParsedData?.length > 0)
            return getInputPriceSimulatorColumns(
                true,
                false,
                levels,
                level1Index,
                setLevel1Index,
                level2Index,
                setLevel2Index,
                '',
                () => {},
                () => {},
                () => {},
                () => {},
                inputKpiOptions,
            );
    }, [level1Index, cardDetails?.data?.inputParsedData, level2Index]);

    const outputMemoisedColumns = useMemo(() => {
        if (cardDetails?.data?.outputParsedData?.length > 0) {
            return getOutputPriceSimulatorColumns(
                cardDetails?.data?.outputParsedData,
                levels,
                level1Index,
                setLevel1Index,
                level2Index,
                setLevel2Index,
                '',
                outputKeyMapping,
            );
        }
    }, [cardDetails?.data?.outputParsedData, level1Index, level2Index]);

    const goalsMemoisedColumns = useMemo(() => {
        if (cardDetails?.data?.parsedKpiMetricData?.length > 0) {
            return getGoalsColumnsForSimulatorComparison(cardDetails?.data?.parsedKpiMetricData);
        }
        return [];
    }, [cardDetails?.data?.parsedKpiMetricData]);

    const dateLabel = useMemo(() => {
        let startLabel = 'Start Date';
        let endLabel = 'End Date';
        if (cardDetails?.data?.SimulationDateRange[0])
            startLabel = dayjs(new Date(cardDetails?.data?.SimulationDateRange[0])).format('MM/DD/YYYY');
        if (cardDetails?.data?.SimulationDateRange[1])
            endLabel = dayjs(new Date(cardDetails?.data?.SimulationDateRange[1])).format('MM/DD/YYYY');
        return { startLabel, endLabel };
    }, [cardDetails?.data?.SimulationDateRange]);

    const filterLabel = useMemo(() => {
        let filtersLabel = '';

        if (cardDetails?.data?.GlobalFilters?.length > 0) {
            filtersLabel = '';
            const requiredFilters = cardDetails?.data?.GlobalFilters.filter(
                (item: any) => !['Simulation Period', 'Price Effective date'].includes(item.title),
            );

            requiredFilters.slice(0, 3).forEach((item: any) => {
                filtersLabel += item.title + ': ';

                item.selectedItems.slice(0, 3).forEach((filterkey: string) => {
                    filtersLabel += filterkey + ', ';
                });
                if (item.selectedItems.length > 3) {
                    filtersLabel += ' +' + (item.selectedItems.length - 3) + ' more ';
                }
            });

            if (requiredFilters.length > 3) {
                filtersLabel += ' +' + (requiredFilters.length - 3) + ' more';
            }
        }

        return filtersLabel;
    }, [cardDetails?.data?.GlobalFilters]);

    function initializeInputKpisForMultiSelect() {
        const inputkpiMappingOptions = Object.keys(inputKeyMapping)?.map((kpi: any, index: number) => {
            return {
                label: inputKeyMapping[kpi],
                value: index,
            };
        });

        return inputkpiMappingOptions;
    }

    const commonLoading = cardDetails?.loading;

    const renderComponentBasedOnDisplayTile = () => {
        if (scenarioInfoSelectedTile.key === scenarioInformationTileData[0].key) {
            return (
                <CustomPriceSimulationInputTable
                    bordered
                    style={{
                        opacity: commonLoading ? '0.5' : 1,
                        pointerEvents: commonLoading ? 'none' : 'auto',
                    }}
                    columns={inputMemoisedColumns}
                    pagination={false}
                    scroll={{ x: '100%', y: 400 }}
                    sticky
                    loading={commonLoading}
                    dataSource={cardDetails?.data?.inputParsedData}
                    className="mb-24"
                />
            );
        }

        if (scenarioInfoSelectedTile.key === scenarioInformationTileData[1].key) {
            return (
                <CustomPriceSimulationInputTable
                    bordered
                    style={{
                        opacity: commonLoading ? '0.5' : 1,
                        pointerEvents: commonLoading ? 'none' : 'auto',
                    }}
                    columns={outputMemoisedColumns}
                    pagination={false}
                    scroll={{ x: '100%', y: 400 }}
                    className="mb-24"
                    sticky
                    loading={commonLoading}
                    dataSource={cardDetails?.data?.outputParsedData}
                />
            );
        }

        if (scenarioInfoSelectedTile.key === scenarioInformationTileData[2].key) {
            return (
                <CustomPriceSimulationInputTable
                    bordered
                    style={{
                        opacity: commonLoading ? '0.5' : 1,
                        pointerEvents: commonLoading ? 'none' : 'auto',
                    }}
                    columns={goalsMemoisedColumns}
                    pagination={false}
                    scroll={{ x: '100%', y: 400 }}
                    className="mb-24"
                    sticky
                    loading={commonLoading}
                    dataSource={cardDetails?.data?.parsedKpiMetricData}
                />
            );
        }

        return null;
    };

    const renderExcelDownloadOption = () => {
        if (scenarioInfoSelectedTile.key === scenarioInformationTileData[0].key) {
            const inputKPIMapping = reverseObjectKeyValues(inputKeyMapping);
            const headerLabels = [...levels];
            Object.keys(inputKPIMapping).forEach((k: string) => {
                if (k === 'Comp Follow') {
                    headerLabels.push('Comp Follow');
                } else {
                    if (k === 'List Price') {
                        headerLabels.push('Current List Price');
                    }
                    headerLabels.push('New ' + k);
                    headerLabels.push('Perc Chg');
                }
            });

            return (
                <DownloadExcel
                    title={'ScenarioInputTable'}
                    excelData={priceSimulatorScenarioInputTable(
                        cardDetails?.data?.inputParsedData,
                        Object.keys(inputKPIMapping),
                        levels,
                        true,
                    )}
                    headers={headerLabels}
                    excludeFilters={true}
                />
            );
        }
        if (scenarioInfoSelectedTile.key === scenarioInformationTileData[1].key) {
            const headerLabels = [...levels];
            if (cardDetails?.data?.outputParsedData?.length) {
                Object.keys(cardDetails?.data?.outputParsedData[0]).forEach((item: string) => {
                    if (!['label', 'level', 'index', 'nestedIndex'].includes(item)) headerLabels.push(item);
                });
            }
            return (
                <DownloadExcel
                    title={'ScenarioOutputTable'}
                    excelData={priceSimulatorScenarioOutputTable(
                        cardDetails?.data?.outputParsedData,
                        headerLabels.slice(2),
                        levels,
                    )}
                    headers={headerLabels}
                    excludeFilters={true}
                />
            );
        }
        if (scenarioInfoSelectedTile.key === scenarioInformationTileData[2].key) {
            let headerLabels: string[] = [];
            if (cardDetails?.data?.parsedKpiMetricData?.length) {
                headerLabels = Object.keys(cardDetails?.data?.parsedKpiMetricData[0]);
            }
            return (
                <DownloadExcel
                    title={'ScenarioGoalsTable'}
                    excelData={priceSimulatorScenarioGoalsTable(cardDetails?.data?.parsedKpiMetricData, headerLabels)}
                    headers={headerLabels}
                    excludeFilters={true}
                />
            );
        }
        return null;
    };

    // exports recommendation, input, output tables data
    const getAllThreeTableData = () => {
        let finalData: any[] = [];
        if (cardDetails?.data?.recommendationOutput)
            finalData = getCombinedTableData(
                levels,
                cardDetails?.data?.recommendationOutput,
                cardDetails?.data?.inputParsedData,
                cardDetails?.data?.outputParsedData,
                inputKeyMapping,
                recommendationKeyMapping,
                cardDetails?.scenarioName,
            );

        return (
            <DownloadExcel
                title="All Tables Data"
                completeDataWithHeaders={finalData}
                excelData={undefined}
                headers={[]}
            />
        );
    };

    return (
        <div className="mt-4">
            <div className="flex gap-4 items-center">
                <h2 className={`font-medium m-0 text-headerColor mr-4 font-lg`}>{cardDetails?.scenarioName}</h2>
                <TileButton
                    disabled={false}
                    data={scenarioInformationTileData}
                    activeKey={scenarioInfoSelectedTile.key}
                    onClick={(item: any) => {
                        setScenarioInfoSelectedTile(item);
                        trackEventMixpanel(PSR_COMP_MORE_INFO_TILE_CHANGED, {
                            selectedTile: item.label,
                        });
                    }}
                />
                {!cardDetails?.loading && <div>{getAllThreeTableData()}</div>}
            </div>
            <div className="mt-2 flex justify-between">
                <span className="font-xs text-promoColour ">
                    Simulation Period: {dateLabel.startLabel} - {dateLabel.endLabel}
                </span>
                {!cardDetails?.loading && <span className="flex gap-2">{renderExcelDownloadOption()}</span>}
            </div>
            <div className="font-xs text-promoColour mb-2">{filterLabel}</div>

            <div className=" ">{renderComponentBasedOnDisplayTile()} </div>
        </div>
    );
};

export default ScenarioInformation;
